import React, { useEffect, useMemo, useState, useRef } from "react";
import "../styles/CarwashAppointment.css";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createBooking, resetState } from "../features/booking/bookingSlice";
import { toast } from "react-toastify";

// ✅ PACKAGES
const packages = [
  {
    name: "Body Wash + Vacuum",
    car: 500,
    van: 700,
  },
  {
    name: "Body + Vacuum + Engine",
    car: 700,
    van: 800,
  },
  {
    name: "Body Wash",
    car: 300,
    van: 500,
  },
  {
    name: "Interior Vacuum",
    car: 300,
    van: 300,
  },
  {
    name: "Engine Wash",
    car: 300,
    van: 300,
  },
];

// ✅ VALIDATION (UPDATED)
const schema = yup.object().shape({
  name: yup.string().required("Full name is required"),
  phone: yup.string().required("Phone number is required"),
  vehicleType: yup.string().required("Vehicle type is required"),
  carWashPackage: yup.string().required("Service package is required"),
  preferredDate: yup.string().required("Date is required"),
  preferredTime: yup.string().required("Time is required"),
});

const CarwashAppointment = () => {
  const dispatch = useDispatch();

  const { isLoading, isSuccess, isError, message } = useSelector(
    (state) => state.booking
  );

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    vehicleType: "Car",
    carWashPackage: "Body Wash",
    preferredDate: "",
    preferredTime: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});

  // ✅ PRICE CALCULATION (FIXED)
  const totalPrice = useMemo(() => {
    const selected = packages.find(
      (p) => p.name === formData.carWashPackage
    );

    if (!selected) return 0;

    return formData.vehicleType === "Van"
      ? selected.van
      : selected.car;
  }, [formData.vehicleType, formData.carWashPackage]);

  // ✅ TOAST FEEDBACK
  useEffect(() => {
    if (isSuccess) {
      toast.success("Car wash booked successfully!");

      setFormData({
        name: "",
        phone: "",
        email: "",
        vehicleType: "Car",
        carWashPackage: "Body Wash",
        preferredDate: "",
        preferredTime: "",
        notes: "",
      });

      setErrors({});
      dispatch(resetState());
    }

    if (isError) {
      toast.error(message || "Something went wrong");
      dispatch(resetState());
    }
  }, [isSuccess, isError, message, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await schema.validate(formData, { abortEarly: false });

      const bookingData = {
        serviceType: "carwash",
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        vehicleType: formData.vehicleType,
        carWashPackage: formData.carWashPackage,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        notes: formData.notes,
        price: totalPrice,
      };

      await dispatch(createBooking(bookingData)).unwrap();
    } catch (error) {
      if (error.inner) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });

        setErrors(validationErrors);
        toast.error("Please fill all required fields");
      } else {
        toast.error("Something went wrong");
      }
    }
  };

  // ✅ SCROLL REVEAL
  const servicesRef = useRef(null);

  useEffect(() => {
    const revealElements =
      servicesRef.current?.querySelectorAll("[data-reveal]") || [];

    const scrollReveal = () => {
      revealElements.forEach((el) => {
        const elementIsInScreen =
          el.getBoundingClientRect().top < window.innerHeight / 1.15;

        if (elementIsInScreen) {
          el.classList.add("revealed");
        } else {
          el.classList.remove("revealed");
        }
      });
    };

    window.addEventListener("scroll", scrollReveal);
    scrollReveal();

    return () => {
      window.removeEventListener("scroll", scrollReveal);
    };
  }, []);

  return (
    <main className="carwash-page" ref={servicesRef}>
      {/* HERO */}
      <section className="carwash-hero">
        <span data-reveal>Book your wash</span>
        <h1 data-reveal>Car Wash Appointment</h1>
        <p data-reveal>
          Choose your preferred service and book your car wash in minutes.
        </p>
      </section>

      {/* PRICING */}
      <section className="carwash-pricing" data-reveal>
        <h2>Car Wash Price List</h2>

        <div className="carwash-table-wrapper">
          <table className="carwash-table">
            <thead>
              <tr>
                <th>Package</th>
                <th>Car</th>
                <th>Van</th>
              </tr>
            </thead>

            <tbody>
              {packages.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>KSh {item.car}</td>
                  <td>KSh {item.van}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FORM */}
      <section className="carwash-form-section">
        <div className="form-info">
          <span data-reveal>Appointment form</span>
          <h2 data-reveal>Book Your Car Wash</h2>
        </div>

        <form className="carwash-form" onSubmit={handleSubmit} data-reveal>
          {/* NAME + PHONE */}
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <small>{errors.name}</small>}
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
              {errors.phone && <small>{errors.phone}</small>}
            </div>
          </div>

          {/* VEHICLE + PACKAGE */}
          <div className="form-row">
            <div className="form-group">
              <label>Vehicle Type</label>
              <select
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleChange}
              >
                <option>Car</option>
                <option>Van</option>
              </select>
            </div>

            <div className="form-group">
              <label>Service Package</label>
              <select
                name="carWashPackage"
                value={formData.carWashPackage}
                onChange={handleChange}
              >
                {packages.map((p) => (
                  <option key={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* TOTAL */}
          <div className="form-total">
            Estimated Total: <strong>KSh {totalPrice}</strong>
          </div>

          {/* DATE + TIME */}
          <div className="form-row">
            <div className="form-group">
              <label>Preferred Date</label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
              />
              {errors.preferredDate && (
                <small>{errors.preferredDate}</small>
              )}
            </div>

            <div className="form-group">
              <label>Preferred Time</label>
              <input
                type="time"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
              />
              {errors.preferredTime && (
                <small>{errors.preferredTime}</small>
              )}
            </div>
          </div>

          {/* NOTES ONLY */}
          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              name="notes"
              rows="5"
              placeholder="Any stains or special instructions?"
              value={formData.notes}
              onChange={handleChange}
            ></textarea>
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? "Submitting..." : "Book Appointment"}
          </button>
        </form>
      </section>
    </main>
  );
};

export default CarwashAppointment;